import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  TextField,
  Autocomplete,
  Grid,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
} from '@mui/material';
import {
  SwapVertOutlined,
  SearchOutlined,
  FileDownloadOutlined,
  PictureAsPdfOutlined,
  TableChartOutlined,
  KeyboardArrowDownOutlined,
} from '@mui/icons-material';
import { api } from '../../services/api';
import { Item } from '../../types/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

interface InventoryMovementEntry {
  id: number;
  date: string;
  order_id: number;
  partner_name: string;
  item_name: string;
  quantity: number;
  movement_type: 'INWARD' | 'OUTWARD' | 'RETURN';
}

interface MovementSummary {
  total_inward: number;
  total_outward: number;
  total_return: number;
}

interface PaginatedResponse {
  data: InventoryMovementEntry[];
  limit: number;
  next_id?: number;
}

const ItemMovements: React.FC = () => {
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [startDate, setStartDate] = useState<string>(() => {
    const today = new Date();
    return today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
  });
  const [endDate, setEndDate] = useState<string>(() => {
    const today = new Date();
    return today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0');
  });

  const [allItems, setAllItems] = useState<Item[]>([]);

  const [summary, setSummary] = useState<MovementSummary | null>(null);
  const [movements, setMovements] = useState<InventoryMovementEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextId, setNextId] = useState<number | undefined>(undefined);

  const [currentStock, setCurrentStock] = useState<number | null>(null);

  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api
      .get('/api/items')
      .then((res) => {
        const items = res.data.items || [];
        setAllItems(
          items.map((item: any) => ({
            id: item.id,
            name: item.name,
            category_id: item.category_id,
            category_name: item.category_name,
          }))
        );
      })
      .catch((err) => console.error('Error fetching items:', err));
  }, []);

  const fetchSummary = useCallback(async () => {
    if (!selectedItem) return;

    try {
      const params = new URLSearchParams({
        item_id: String(selectedItem.id),
        category_id: String(selectedItem.category_id),
        start_date: startDate,
        end_date: endDate,
      });

      const response = await api.get<{ summary: MovementSummary }>(`/api/stock-movements/inventory/summary?${params}`);
      setSummary(response.data.summary || null);
    } catch (error) {
      console.error('Error fetching summary:', error);
      setSummary(null);
    }
  }, [selectedItem, startDate, endDate]);

  const fetchMovements = useCallback(
    async (beforeId?: number) => {
      if (!selectedItem) return;

      const isLoadingMore = beforeId !== undefined;
      if (isLoadingMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const params = new URLSearchParams({
          item_id: String(selectedItem.id),
          category_id: String(selectedItem.category_id),
          start_date: startDate,
          end_date: endDate,
          limit: '15',
        });

        if (beforeId) params.append('before_id', beforeId.toString());

        const response = await api.get<PaginatedResponse>(`/api/stock-movements/inventory/paginated?${params}`);
        const newMovements = response.data.data || [];

        if (isLoadingMore) {
          setMovements((prev) => [...prev, ...newMovements]);
        } else {
          setMovements(newMovements);
        }

        const hasNext = response.data.next_id !== undefined && newMovements.length > 0;
        setHasMoreData(hasNext);
        setNextId(response.data.next_id);
      } catch (error) {
        console.error('Error fetching inventory movements:', error);
        if (!isLoadingMore) {
          setMovements([]);
        }
        setHasMoreData(false);
      } finally {
        if (isLoadingMore) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [selectedItem, startDate, endDate]
  );

  const fetchItemStock = async (): Promise<number> => {
    try {
      const response = await api.get(`/api/items/${selectedItem?.id}/stock`);
      return response.data.available_quantity ?? 0;
    } catch (err) {
      console.error('Error fetching stock:', err);
      return 0;
    }
  };

  const handleSearch = () => {
    setMovements([]);
    setHasMoreData(false);
    setNextId(undefined);
    setHasSearched(true);
    Promise.all([fetchSummary(), fetchMovements(), fetchItemStock().then(setCurrentStock)]);
  };

  const handleLoadMore = () => {
    if (nextId !== undefined) {
      fetchMovements(nextId);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getMovementTypeChip = (type: string) => {
    const chipProps: { label: string; color: 'success' | 'error' | 'warning' } = {
      label: type,
      color: 'warning',
    };

    switch (type) {
      case 'INWARD':
        chipProps.color = 'success';
        break;
      case 'OUTWARD':
        chipProps.color = 'error';
        break;
      case 'RETURN':
        chipProps.color = 'warning';
        break;
    }

    return <Chip label={chipProps.label} color={chipProps.color} size="small" />;
  };

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const fetchAllForExport = async (): Promise<InventoryMovementEntry[]> => {
    if (!selectedItem) return [];

    try {
      const allMovements: InventoryMovementEntry[] = [];
      let currentBeforeId: number | undefined = undefined;

      while (true) {
        const params = new URLSearchParams({
          item_id: String(selectedItem.id),
          category_id: String(selectedItem.category_id),
          start_date: startDate,
          end_date: endDate,
          limit: '500',
        });

        if (currentBeforeId) params.append('before_id', currentBeforeId.toString());

        const response = await api.get<PaginatedResponse>(`/api/stock-movements/inventory/paginated?${params}`);
        const pageData = response.data.data || [];
        allMovements.push(...pageData);

        if (!response.data.next_id || pageData.length === 0) {
          break;
        }

        currentBeforeId = response.data.next_id;
      }

      return allMovements;
    } catch (error) {
      console.error('Error fetching data for export:', error);
      return [];
    }
  };

  const exportToPDF = async () => {
    handleExportClose();
    setExporting(true);

    try {
      const data = await fetchAllForExport();
      if (data.length === 0) {
        alert('No data to export');
        return;
      }

      const doc = new jsPDF();

      doc.setFontSize(11);
      doc.text('Item Movements Report', 14, 8);

      doc.setFontSize(7);
      doc.text(`Item: ${selectedItem?.name || ''}`, 14, 13);
      doc.text(`Period: ${startDate} to ${endDate}`, 14, 17);

      let currentY = 21;
      doc.setFontSize(8);
      doc.text('Summary', 14, currentY);
      currentY += 4;
      doc.setFontSize(7);
      doc.text(`Total Inward: ${Math.abs(summary?.total_inward || 0) +Math.abs(summary?.total_return || 0)}`, 14, currentY);
      currentY += 3;
      doc.text(`Total Outward: ${Math.abs(summary?.total_outward || 0)}`, 14, currentY);
      currentY += 5;

      const tableData = data.map((movement) => [
        formatDate(movement.date),
        `#${movement.order_id}`,
        movement.partner_name,
        movement.item_name,
        ['INWARD', 'RETURN'].includes(movement.movement_type) ? Math.abs(movement.quantity).toString() : '-',
        movement.movement_type === 'OUTWARD' ? Math.abs(movement.quantity).toString() : '-',
        movement.movement_type,
      ]);

      autoTable(doc, {
        startY: currentY,
        head: [['Date', 'Order ID', 'Partner Name', 'Item Name', 'Qty In', 'Qty Out', 'Movement Type']],
        body: tableData,
        styles: {
          fontSize: 6,
          cellPadding: 1,
          lineColor: [200, 200, 200],
          lineWidth: 0.1,
        },
        headStyles: {
          fontSize: 6,
          fontStyle: 'bold',
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
        },
        tableLineColor: [200, 200, 200],
        tableLineWidth: 0.1,
      });

      doc.save(`item-movement-${selectedItem?.name || 'item'}-${startDate}-to-${endDate}.pdf`);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      alert('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const exportToExcel = async () => {
    handleExportClose();
    setExporting(true);

    try {
      const data = await fetchAllForExport();
      if (data.length === 0) {
        alert('No data to export');
        return;
      }

      const worksheetData: (string | number)[][] = [
        ['Item Movements Report'],
        [`Item: ${selectedItem?.name || ''}`],
        [`Period: ${startDate} to ${endDate}`],
        [],
        ['Summary'],
        [`Total Inward: ${Math.abs(summary?.total_inward || 0) + Math.abs(summary?.total_return || 0)}`],
        [`Total Outward: ${Math.abs(summary?.total_outward || 0)}`],
        [],
        ['Date', 'Order ID', 'Partner Name', 'Item Name', 'Qty In', 'Qty Out', 'Movement Type'],
        ...data.map((movement) => [
          formatDate(movement.date),
          movement.order_id,
          movement.partner_name,
          movement.item_name,
          ['INWARD', 'RETURN'].includes(movement.movement_type) ? Math.abs(movement.quantity) : '-',
          movement.movement_type === 'OUTWARD' ? Math.abs(movement.quantity) : '-',
          movement.movement_type,
        ]),
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      worksheet['!cols'] = [
        { wch: 20 },
        { wch: 12 },
        { wch: 25 },
        { wch: 25 },
        { wch: 10 },
        { wch: 10 },
        { wch: 15 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Item Movement');

      XLSX.writeFile(workbook, `item-movement-${selectedItem?.name || 'item'}-${startDate}-to-${endDate}.xlsx`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  };

  const canSearch = selectedItem !== null && startDate && endDate;

  return (
    <Paper sx={{ p: 2, m: 0 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SwapVertOutlined sx={{ fontSize: 28, mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight="bold">
            Item Movements
          </Typography>
        </Box>
        {hasSearched && !loading && movements.length > 0 && (
          <>
            <Button
              variant="outlined"
              size="small"
              startIcon={exporting ? <CircularProgress size={16} /> : <FileDownloadOutlined />}
              onClick={handleExportClick}
              disabled={exporting}
            >
              Export
            </Button>
            <Menu
              anchorEl={exportAnchorEl}
              open={Boolean(exportAnchorEl)}
              onClose={handleExportClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={exportToPDF}>
                <ListItemIcon>
                  <PictureAsPdfOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export as PDF</ListItemText>
              </MenuItem>
              <MenuItem onClick={exportToExcel}>
                <ListItemIcon>
                  <TableChartOutlined fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export as Excel</ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select items and a date range to view their inventory movements.
      </Typography>

      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Autocomplete
            options={allItems}
            getOptionLabel={(option) => option.name}
            value={selectedItem}
            onChange={(_event, newValue) => setSelectedItem(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Item *"
                placeholder="Select item"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <TextField
            size="small"
            fullWidth
            label="Start Date *"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <TextField
            size="small"
            fullWidth
            label="End Date *"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSearch}
            disabled={!canSearch || loading}
            startIcon={<SearchOutlined />}
            sx={{ height: '40px' }}
          >
            Search
          </Button>
        </Grid>
      </Grid>

      {/* Summary Cards */}
      {loading && !hasSearched ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : hasSearched && summary ? (
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          {currentStock !== null && (
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ bgcolor: '#e3f2fd', borderLeft: '4px solid #1976d2' }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="textSecondary">
                      Current Stock:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ color: '#1565c0' }}>
                      {currentStock}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card sx={{ bgcolor: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    Total Quantity In:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: '#2e7d32' }}>
                    {Math.abs(summary.total_inward)+Math.abs(summary.total_return)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Card sx={{ bgcolor: '#fff3e0', borderLeft: '4px solid #ff9800' }}>
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    Total Quantity Out:
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: '#e65100' }}>
                    {Math.abs(summary.total_outward)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : null}

      {/* Results */}
      {loading && hasSearched ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : hasSearched ? (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Order ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Partner Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Item Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    Quantity In
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    Quantity Out
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">
                    Movement Type
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {movements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No movements found for the selected items and date range
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  movements.map((movement, index) => (
                    <TableRow key={`${movement.id}-${index}`} hover>
                      <TableCell>
                        <Typography variant="body2">{formatDate(movement.date)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" sx={{ color: 'primary.main' }}>
                          #{movement.order_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{movement.partner_name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{movement.item_name}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {["INWARD", "RETURN"].includes(movement.movement_type)? Math.abs(movement.quantity): "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {movement.movement_type === "OUTWARD" ? Math.abs(movement.quantity) : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{getMovementTypeChip(movement.movement_type)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {hasMoreData && movements.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                disabled={loadingMore}
                endIcon={loadingMore ? <CircularProgress size={16} /> : <KeyboardArrowDownOutlined />}
              >
                {loadingMore ? 'Loading...' : 'Load More'}
              </Button>
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Select items and a date range, then click Search to view movements
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ItemMovements;
